import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useLazyQuery } from "@apollo/client/react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "./i18n/I18nProvider";
import { Language } from "./i18n/translations";
import { faPhone, faMapMarkerAlt, faArrowRight, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import "./App.css";
import { Plan } from "./types";
import {
  HouseholdType,
  LeadHousehold,
  LeadHouseholdMember,
  LeadWizardMetadata,
  WizardPrimaryApplicant,
  WizardStep,
  ConsentStatus,
  QuoteHolderFormState,
  QuoteMemberFormState,
  QuotePaymentFormState,
} from "./types/leadWizard";
import {
  parseMetadata,
  emptyMetadata,
  householdNeedsAdditionalMembers,
  isHouseholdComplete,
  primaryFromMetadata,
  identityStatusFromMetadata,
} from "./utils/leadMetadata";
import {
  PUBLIC_START_LEAD,
  PUBLIC_UPDATE_HOUSEHOLD,
  PUBLIC_CONFIRM_PLAN,
  PUBLIC_CHECK_CONSENT,
  PUBLIC_INIT_IDENTITY,
  PUBLIC_VERIFY_IDENTITY_OTP,
  PUBLIC_CREATE_QUOTE,
  PUBLIC_UPDATE_LEAD_STAGE,
} from "./gql/publicLead";
import {
  PUBLIC_ZIPCODE_BY_ZIP,
  PUBLIC_ZIPCODE_SUGGESTIONS,
} from "./gql/publicZipcode";
import {
  PublicZipcodeResponse,
  PublicZipcodeSuggestionsResponse,
  ZipcodeSuggestion,
  ZipcodeByZip,
} from "./gql/types/IResponseZipCode";
import {
  PublicCheckConsentResponse,
  PublicConfirmPlanResponse,
  PublicStartLeadResponse,
  PublicUpdateHouseholdResponse,
  PublicInitIdentityResponse,
  PublicVerifyIdentityOtpResponse,
  PublicCreateQuoteResponse,
  PublicUpdateLeadStageResponse,
} from "./gql/types/PublicLead";
import { HouseholdTypeStep } from "./components/HouseholdTypeStep";
import { CoverageTypeStep } from "./components/CoverageTypeStep";
import { HouseholdBasicsStep } from "./components/HouseholdBasicsStep";
import { PrimaryApplicantStep } from "./components/PrimaryApplicantStep";
import { AdditionalMembersStep } from "./components/AdditionalMembersStep";
import { PlanSelectionPage } from "./pages/PlanSelectionPage";
import { PlanSummaryPage } from "./components/PlanSummaryPage";
import { QuoteOtpStep } from "./components/QuoteOtpStep";
import { QuoteHolderStep } from "./components/QuoteHolderStep";
import { QuoteMembersStep } from "./components/QuoteMembersStep";
import { QuotePaymentStep } from "./components/QuotePaymentStep";
import { QuoteSuccessStep } from "./components/QuoteSuccessStep";
import { pushToDataLayer, trackFormSubmit, trackPhoneCall, normalizeEmail, normalizePhone, isDebugEnabled } from "./utils/gtm";
import { getRecaptchaToken } from "./utils/recaptcha";
import { decodePublicTokenConfig, getPublicAccessToken } from "./utils/publicToken";
import { OPTIONS } from "./consts";

// Extender Window para gtag
declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    gtag?: (...args: unknown[]) => void;
  }
}

// Inicializar gtag en desarrollo si no está disponible (fallback para local sin CloudFront)
const initGtagFallback = () => {
  if (typeof window !== 'undefined') {
    const isLocal =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      process.env.NODE_ENV === 'development';

    window.dataLayer = window.dataLayer || [];

    // Si no existe, o si es local (para poder interceptar/mockear y ver logs en consola)
    if (!window.gtag || isLocal) {
      const originalGtag = window.gtag;
      
      window.gtag = function (...args: unknown[]) {
        // Ejecutar original si existe (para acumular en dataLayer)
        if (originalGtag) {
          try {
            originalGtag(...args);
          } catch (e) {
            window.dataLayer?.push(args as any);
          }
        } else {
          window.dataLayer?.push(args as any);
        }

        // Logs legibles en consola para desarrollo
        if (isLocal) {
          console.log('[gtag fallback]', args);

          // Si es la configuración de tracking de llamadas, simulamos el callback de Google Ads
          if (
            args[0] === 'config' &&
            args[2] &&
            typeof args[2] === 'object'
          ) {
            const options = args[2] as Record<string, unknown>;
            if (typeof options.phone_conversion_callback === 'function') {
              const callback = options.phone_conversion_callback as (
                formattedNumber: string,
                mobileNumber: string
              ) => void;
              
              // Simular retardo de Google Ads cargando/reemplazando el número
              setTimeout(() => {
                const mockGfn = "(800) 555-0199";
                console.log(
                  `%c[gtag fallback] 📞 Simulando Google Forwarding Number replacement: ${mockGfn}`,
                  "color: #10B981; font-weight: bold; background: #ECFDF5; padding: 2px 6px;"
                );
                callback(mockGfn, "8005550199");
              }, 600);
            }
          }
        }
      };

      if (!originalGtag) {
        window.gtag('js', new Date());
      }
    }
  }
};

// Ejecutar al cargar el módulo
initGtagFallback();

const STEP_TITLE_KEYS: Record<WizardStep, string> = {
  householdType: "wizard.titles.householdType",
  householdBasics: "wizard.titles.householdBasics",
  primary: "wizard.titles.primary",
  members: "wizard.titles.members",
  identityOtp: "wizard.titles.identityOtp",
  plans: "wizard.titles.plans",
  summary: "wizard.titles.summary",
  consent: "wizard.titles.consent",
  extra: "wizard.titles.extra",
  quoteHolder: "wizard.titles.quoteHolder",
  quoteMembers: "wizard.titles.quoteMembers",
  quotePayment: "wizard.titles.quotePayment",
  quoteSuccess: "wizard.titles.quoteSuccess",
};

interface PlanSelectionContext {
  fetchedAt: string;
  count: number;
  year: number;
}




const buildPlanSelectionInput = (plan: Plan) => ({
  planId: plan.id,
  issuer: plan.carrier,
  premium: plan.monthlyPremium,
  deductible: plan.deductible,
  metalLevel: plan.metalLevel ?? null,
  productType: plan.productType ?? null,
  name: plan.name,
});

const consentFromMetadata = (metadata: LeadWizardMetadata): ConsentStatus => {
  const status = metadata.consent?.status;
  if (!status) {
    return "NOT_REQUESTED";
  }
  return status as ConsentStatus;
};

const quoteRelationshipFromRole = (role?: string | null) => {
  if (!role) return "Other Relationship";
  const normalized = role.toUpperCase();
  if (normalized === "SPOUSE") return "Spouse";
  if (normalized === "DEPENDENT") return "Child";
  if (normalized === "PRIMARY") return "Self";
  return "Other Relationship";
};

const buildQuoteHolderState = (
  metadata: LeadWizardMetadata,
  primary: WizardPrimaryApplicant | LeadHouseholdMember | null
): QuoteHolderFormState => {
  const household = metadata.household || {};
  const source = primary
    ? primary
    : primaryFromMetadata(metadata) ?? {
      firstName: "",
      lastName: "",
      gender: "F" as const,
      birthDate: "",
      email: "",
      phone: "",
      acceptsTerms: false,
      whatsappConsent: false,
    };

  const firstName = (source as any).firstName ?? "";
  const lastName = (source as any).lastName ?? "";
  const gender = ((source as any).gender ?? "F") as "M" | "F" | "X";
  const birthDate = (source as any).birthDate ?? "";
  const email = (source as any).email ?? "";
  const phone = (source as any).phone ?? "";

  const stateCode = (household.stateId as string | undefined) ?? "";
  const stateName = (household.stateName as string | undefined) ?? "";
  const zipCode = (household.zipCode as string | undefined) ?? "";

  return {
    firstName,
    lastName,
    gender,
    birthDate,
    email,
    phone,
    address1: "",
    address2: "",
    city: "",
    stateCode: stateCode.toUpperCase(),
    stateName,
    zipCode,
    countyFips: (household.countyFips as string | undefined) ?? "",
    ssn: "",
    usesTobacco: false,
    isPregnant: false,
  };
};

const buildQuoteMembersState = (
  metadata: LeadWizardMetadata
): QuoteMemberFormState[] => {
  const members = metadata.members || [];
  if (!members.length) {
    return [];
  }
  return members.slice(1).map((member) => ({
    firstName: member.firstName,
    lastName: member.lastName,
    gender: (member.gender as "M" | "F" | "X") ?? "F",
    birthDate: member.birthDate,
    relationship: quoteRelationshipFromRole(member.role),
    email: member.email ?? "",
    phone: member.phone ?? "",
    usesTobacco: false,
    isPregnant: false,
  }));
};

const buildQuotePaymentState = (
  holder: QuoteHolderFormState
): QuotePaymentFormState => ({
  method: "CARD",
  cardNumber: "",
  expirationMonth: "",
  expirationYear: "",
  cvv: "",
  cardType: "CREDIT_CARD",
  cardCompany: "",
  bankName: "",
  abaRouting: "",
  accountNumber: "",
  paymentHolder: `${holder.firstName} ${holder.lastName}`.trim(),
  billingAddress1: holder.address1,
  billingAddress2: holder.address2,
  billingCity: holder.city,
  billingStateCode: holder.stateCode,
  billingZipCode: holder.zipCode,
  confirmAccuracy: false,
  providePaymentLater: false,
});

const buildNoPaymentState = (
  holder: QuoteHolderFormState
): QuotePaymentFormState => ({
  method: "NONE",
  cardNumber: "",
  expirationMonth: "",
  expirationYear: "",
  cvv: "",
  cardType: "",
  cardCompany: "",
  bankName: "",
  abaRouting: "",
  accountNumber: "",
  paymentHolder: `${holder.firstName} ${holder.lastName}`.trim(),
  billingAddress1: holder.address1,
  billingAddress2: holder.address2,
  billingCity: holder.city,
  billingStateCode: holder.stateCode,
  billingZipCode: holder.zipCode,
  confirmAccuracy: true,
  providePaymentLater: true,
});

const RECENT_CITIES = [
  "Chicago, IL",
  "San Antonio, TX",
  "Tampa, FL",
  "Las Vegas, NV",
  "Miami, FL",
  "Houston, TX",
  "Orlando, FL",
  "Dallas, TX",
];

const App: React.FC = () => {
  const { t, language, setLanguage } = useTranslation();
  const isStaticMode = useMemo(() => {
    return typeof document !== "undefined" && !!document.getElementById("quote-wizard") && !document.getElementById("root");
  }, []);

  const [currentCityIndex, setCurrentCityIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCityIndex((prev) => (prev + 1) % RECENT_CITIES.length);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const [wizardStep, setWizardStep] = useState<WizardStep>("householdType");
  const [metadata, setMetadata] = useState<LeadWizardMetadata>(emptyMetadata());
  const [householdType, setHouseholdType] = useState<HouseholdType | null>(
    null
  );
  const [leadId, setLeadId] = useState<string | null>(null);
  const [heroZip, setHeroZip] = useState("");
  const [heroZipSuggestions, setHeroZipSuggestions] = useState<ZipcodeSuggestion[]>([]);
  const [showHeroSuggestions, setShowHeroSuggestions] = useState(false);
  const heroSuggestionsRef = useRef<HTMLDivElement | null>(null);

  const [fetchZipcode] = useLazyQuery<
    PublicZipcodeResponse,
    { zipCode: string; token: string }
  >(PUBLIC_ZIPCODE_BY_ZIP, { fetchPolicy: "no-cache" });

  const [fetchZipcodeSuggestions] = useLazyQuery<
    PublicZipcodeSuggestionsResponse,
    { prefix: string; token: string; limit?: number }
  >(PUBLIC_ZIPCODE_SUGGESTIONS, { fetchPolicy: "network-only" });

  const [primaryApplicant, setPrimaryApplicant] =
    useState<WizardPrimaryApplicant | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [planContext, setPlanContext] = useState<PlanSelectionContext | null>(
    null
  );
  const [signingLink, setSigningLink] = useState<string | null>(null);
  const [consentHtml, setConsentHtml] = useState<string | null>(null);
  const [consentStatus, setConsentStatus] =
    useState<ConsentStatus>("NOT_REQUESTED");
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingConsent, setIsCheckingConsent] = useState(false);
  const [identityToken, setIdentityToken] = useState<string | null>(null);
  const [identityOtpStatus, setIdentityOtpStatus] = useState({
    sent: false,
    smsSent: false,
    emailSent: false,
  });
  const [isIdentityOtpSending, setIsIdentityOtpSending] = useState(false);
  const [isIdentityOtpVerifying, setIsIdentityOtpVerifying] = useState(false);
  const consentTrackedLeadRef = useRef<string | null>(null);
  const leadStageRef = useRef<{ leadId: string | null; stageId: number | null }>(
    {
      leadId: null,
      stageId: null,
    }
  );

  const [quoteHolderState, setQuoteHolderState] =
    useState<QuoteHolderFormState>(() =>
      buildQuoteHolderState(metadata, primaryApplicant)
    );
  const [quoteMembersState, setQuoteMembersState] = useState<
    QuoteMemberFormState[]
  >(() => buildQuoteMembersState(metadata));
  const [quotePaymentState, setQuotePaymentState] =
    useState<QuotePaymentFormState>(() =>
      buildQuotePaymentState(buildQuoteHolderState(metadata, primaryApplicant))
    );
  const [isCreatingQuote, setIsCreatingQuote] = useState(false);
  const [quotePolicyId, setQuotePolicyId] = useState<string | null>(null);
  const [requiresPayment, setRequiresPayment] = useState<boolean>(true);

  const currentPrimary = primaryApplicant || primaryFromMetadata(metadata);

  const publicToken = useMemo(() => getPublicAccessToken(), []);

  const resolvedHouseholdType = (householdType ||
    (metadata.household.type as HouseholdType | null)) as HouseholdType | null;
  const identityVerified = identityStatusFromMetadata(metadata) === "VERIFIED";

  const planSelection = metadata.plan?.selection;
  const successPlanName =
    selectedPlan?.name ??
    (planSelection && typeof planSelection.name === "string"
      ? planSelection.name
      : null);
  const planSelectionCarrier =
    typeof planSelection?.issuer === "string"
      ? planSelection.issuer
      : typeof (planSelection as any)?.carrier === "string"
        ? (planSelection as any).carrier
        : null;
  const successPlanCarrier = selectedPlan?.carrier ?? planSelectionCarrier;
  const selectionPremiumRaw = planSelection?.premium;
  const selectionPremiumNumber =
    typeof selectionPremiumRaw === "number"
      ? selectionPremiumRaw
      : selectionPremiumRaw !== undefined && selectionPremiumRaw !== null
        ? Number(selectionPremiumRaw)
        : null;
  const successPlanPremium =
    selectedPlan?.monthlyPremium ??
    (selectionPremiumNumber !== null && Number.isFinite(selectionPremiumNumber)
      ? selectionPremiumNumber
      : null);

  useEffect(() => {
    if (
      ["quoteHolder", "quoteMembers", "quotePayment", "quoteSuccess"].includes(
        wizardStep
      )
    ) {
      return;
    }
    const baseHolder = buildQuoteHolderState(metadata, currentPrimary ?? null);
    setQuoteHolderState(baseHolder);
    setQuoteMembersState(buildQuoteMembersState(metadata));
    setQuotePaymentState(buildQuotePaymentState(baseHolder));
  }, [metadata, currentPrimary, wizardStep]);

  useEffect(() => {
    if (selectedPlan && typeof selectedPlan.monthlyPremium === "number") {
      setRequiresPayment(selectedPlan.monthlyPremium > 0);
      return;
    }

    const premium = metadata.plan?.selection?.premium;
    if (premium !== undefined && premium !== null) {
      setRequiresPayment(Number(premium) > 0);
    }
  }, [selectedPlan, metadata]);

  useEffect(() => {
    // console.log(wizardStep);
    if (!requiresPayment && wizardStep === "quotePayment") {
      setWizardStep(
        quoteMembersState.length > 0 ? "quoteMembers" : "quoteHolder"
      );
    }
  }, [requiresPayment, wizardStep, quoteMembersState.length]);

  useEffect(() => {
    if (!requiresPayment) {
      setQuotePaymentState((prev) => {
        const next = buildNoPaymentState(quoteHolderState);
        if (
          prev.method === next.method &&
          prev.paymentHolder === next.paymentHolder &&
          prev.billingAddress1 === next.billingAddress1 &&
          prev.billingAddress2 === next.billingAddress2 &&
          prev.billingCity === next.billingCity &&
          prev.billingStateCode === next.billingStateCode &&
          prev.billingZipCode === next.billingZipCode &&
          prev.confirmAccuracy === next.confirmAccuracy &&
          prev.providePaymentLater === next.providePaymentLater
        ) {
          return prev;
        }
        return next;
      });
    }
  }, [requiresPayment, quoteHolderState]);

  useEffect(() => {
    if (requiresPayment) {
      setQuotePaymentState((prev) =>
        prev.method === "NONE" ? buildQuotePaymentState(quoteHolderState) : prev
      );
    }
  }, [requiresPayment, quoteHolderState]);

  const buildPrimaryMember = (
    applicant: WizardPrimaryApplicant
  ): LeadHouseholdMember => ({
    role: "PRIMARY",
    firstName: applicant.firstName.trim(),
    lastName: applicant.lastName.trim(),
    gender: applicant.gender,
    birthDate: applicant.birthDate,
    email: applicant.email.trim(),
    phone: applicant.phone.trim(),
  });

  const mapMemberToInput = (member: LeadHouseholdMember) => ({
    role: member.role,
    firstName: member.firstName,
    lastName: member.lastName,
    gender: member.gender,
    birthDate: member.birthDate,
    email: member.email ?? null,
    phone: member.phone ?? null,
  });

  const submitStartLead = async (
    membersList: LeadHouseholdMember[],
    applicant: WizardPrimaryApplicant,
    householdPayload: LeadHousehold
  ): Promise<LeadWizardMetadata | null> => {
    setIsSubmitting(true);
    setApiError(null);

    try {
      const captchaToken = await getRecaptchaToken("public_start_lead");

      const additionalMembers = membersList.filter(
        (member) => member.role !== "PRIMARY"
      );
      const { data } = await startLeadMutation({
        variables: {
          token: publicToken,
          household: {
            type: householdPayload.type,
            zipCode: householdPayload.zipCode,
            income: householdPayload.income,
            countyFips: householdPayload.countyFips,
            countyName: householdPayload.countyName,
            stateId: householdPayload.stateId,
            stateName: householdPayload.stateName,
            effectiveDate: householdPayload.effectiveDate,
          },
          primary: {
            firstName: applicant.firstName,
            lastName: applicant.lastName,
            gender: applicant.gender,
            birthDate: applicant.birthDate,
            phone: applicant.phone,
            email: applicant.email,
            acceptsTerms: applicant.acceptsTerms,
            whatsappConsent: applicant.whatsappConsent,
          },
          members: additionalMembers.map(mapMemberToInput),
          context: {
            leadSourceId: leadConfig.leadSourceId,
            stagePipelineId: leadConfig.stagePipelineId,
            campusId: leadConfig.campusId,
          },
          captchaToken,
        },
      });

      const response = data?.publicStartLead;
      if (!response?.success || !response.leadId) {
        const message =
          response?.errors?.[0]?.message || t("wizard.errors.generic");
        setApiError(message);
        return null;
      }

      const nextMetadata = parseMetadata(response.metadata);
      setMetadata(nextMetadata);
      setLeadId(response.leadId);
      setConsentStatus(consentFromMetadata(nextMetadata));
      setIdentityToken(null);
      setIdentityOtpStatus({ sent: false, smsSent: false, emailSent: false });
      setIsIdentityOtpSending(false);
      setIsIdentityOtpVerifying(false);
      setSigningLink(null);
      setConsentHtml(null);

      const nextType =
        (nextMetadata.household.type as HouseholdType | null) ??
        resolvedHouseholdType;
      setHouseholdType(nextType);
      leadStageRef.current = { leadId: response.leadId, stageId: null };
      void updateLeadStage(
        leadConfig.stagePipelineIntentId ??
        leadConfig.stagePipelineId ??
        null,
        response.leadId
      );
      const normEmail = normalizeEmail(applicant.email);
      const normPhone = normalizePhone(applicant.phone);
      const zipCode = householdPayload.zipCode || metadata.household.zipCode;
      const region = householdPayload.stateId || metadata.household.stateId;
      const city = householdPayload.countyName || metadata.household.countyName;

      const userData = normEmail || normPhone
        ? {
          user_data: {
            ...(normEmail && { email: normEmail }),
            ...(normPhone && { phone_number: normPhone }),
            ...(applicant.firstName && { first_name: applicant.firstName.trim() }),
            ...(applicant.lastName && { last_name: applicant.lastName.trim() }),
            ...(applicant.birthDate && { date_of_birth: applicant.birthDate }),
            country: "US", // Siempre "US" para Obamacare/ACA
            ...(zipCode && { postal_code: zipCode }),
            ...(region && { region: region.toUpperCase() }),
            ...(city && { city: city.trim() }),
          }
        }
        : {};

      pushToDataLayer({
        event: "lead_started",
        leadId: response.leadId,
        lead_id: response.leadId,
        transaction_id: response.leadId,
        language,
        ...(nextType ? { householdType: nextType } : {}),
        ...userData,
      });
      return nextMetadata;
    } catch (error: any) {
      const fallback = t("wizard.errors.generic");
      const message =
        typeof error?.message === "string" && error.message.trim().length > 0
          ? error.message
          : fallback;
      if (message.toLowerCase().includes("captcha")) {
        setApiError("No pudimos validar el captcha. Intenta nuevamente.");
      } else {
        setApiError(message);
      }
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const leadConfig = useMemo(() => decodePublicTokenConfig(), []);
  const displayPhone = leadConfig.phoneNumber || t("header.phone");
  const phoneDigits = displayPhone.replace(/\D/g, "");
  const phoneHref = `tel:+${phoneDigits.length === 10 ? `1${phoneDigits}` : phoneDigits}`;
  const displayWhatsApp = leadConfig.whatsappNumber || "18586096799";
  const cleanWhatsApp = displayWhatsApp.replace(/\D/g, "");

  const conversionIdRef = useRef<string>('');
  const [activePhone, setActivePhone] = useState(displayPhone);
  const [activePhoneHref, setActivePhoneHref] = useState(phoneHref);

  useEffect(() => {
    setActivePhone(displayPhone);
    setActivePhoneHref(phoneHref);
  }, [displayPhone, phoneHref]);

  useEffect(() => {
    // Obtener el Google Ads ID y teléfono según el idioma actual
    const googleAdsConfigByLanguage: Record<string, { id: string; phone: string }> = {
      en: {
        id: leadConfig.googleAdsIdEn || process.env.REACT_APP_GOOGLE_ADS_CALL_CONVERSION_ID_EN || '',
        phone: leadConfig.googleAdsPhoneEn || process.env.REACT_APP_GOOGLE_ADS_PHONE_EN || '',
      },
      es: {
        id: leadConfig.googleAdsIdEs || process.env.REACT_APP_GOOGLE_ADS_ID_ES || '',
        phone: leadConfig.googleAdsPhoneEs || process.env.REACT_APP_GOOGLE_ADS_PHONE_ES || '',
      },
    };
    const config = googleAdsConfigByLanguage[language];
    conversionIdRef.current = config.id;

    // Verificar si la librería global de Google (gtag) ya cargó en la ventana
    if (typeof (window as any).gtag === 'function' && config.id && (config.phone || displayPhone)) {
      if (isDebugEnabled()) {
        console.log(
          `%c[Google Ads Call Tracking] 📞 Configurando Call Tracking para Google Ads ID: ${config.id}`,
          "color: #F59E0B; font-weight: bold; background: #FEF3C7; padding: 2px 6px; border-radius: 4px;",
          { phone_to_replace: displayPhone || config.phone }
        );
      }

      // Registrar el callback de Google Ads Call Tracking oficial
      (window as any).gtag('config', config.id, {
        'phone_conversion_number': displayPhone || config.phone,
        'phone_conversion_callback': (formattedNumber: string, mobileNumber: string) => {
          if (isDebugEnabled()) {
            console.log(
              `%c[Google Ads Call Tracking] 🎯 ¡Callback Ejecutado! Google Forwarding Number: ${formattedNumber}`,
              "color: #10B981; font-weight: bold; font-size: 13px; background: #ECFDF5; padding: 4px 8px; border-radius: 4px; border: 1px solid #10B981;",
              { formattedNumber, mobileNumber }
            );
          }
          if (formattedNumber) {
            setActivePhone(formattedNumber);
          }
          if (mobileNumber) {
            setActivePhoneHref(`tel:${mobileNumber}`);
          } else if (formattedNumber) {
            const digits = formattedNumber.replace(/\D/g, "");
            setActivePhoneHref(`tel:+${digits.length === 10 ? `1${digits}` : digits}`);
          }
        }
      });
    }
  }, [language, displayPhone]);

  // Synchronize dynamic phone text and links to static DOM elements
  useEffect(() => {
    document.querySelectorAll('.dynamic-phone-text').forEach((el) => {
      el.textContent = activePhone;
    });
  }, [activePhone]);

  useEffect(() => {
    const cleanups: (() => void)[] = [];
    document.querySelectorAll('.dynamic-phone-link').forEach((el) => {
      el.setAttribute('href', activePhoneHref);
      const handler = () => {
        trackPhoneCall(activePhone, language, conversionIdRef.current);
      };
      el.addEventListener('click', handler);
      cleanups.push(() => el.removeEventListener('click', handler));
    });
    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [activePhoneHref, activePhone, language]);

  // Synchronize dynamic WhatsApp link to static DOM elements
  useEffect(() => {
    const displayWhatsApp = leadConfig.whatsappNumber || "18586096799";
    const cleanWhatsApp = displayWhatsApp.replace(/\D/g, "");
    const waText = language === "es"
      ? "Hola,%20me%20interesa%20obtener%20una%20cotización%20de%20seguro%20de%20salud."
      : "Hello,%20I%20am%20interested%20in%20getting%20a%20health%20insurance%20quote.";
    
    document.querySelectorAll('.whatsapp-dynamic-link').forEach((el) => {
      el.setAttribute('href', `https://wa.me/${cleanWhatsApp}?text=${waText}`);
    });
  }, [leadConfig.whatsappNumber, language]);

  // Dismiss global premium loading splash screen once React is mounted and running
  useEffect(() => {
    const loader = document.getElementById("global-loader");
    if (loader) {
      loader.classList.add("fade-out");
      const timeout = setTimeout(() => {
        loader.remove();
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, []);

  // Capture Hero ZIP submit event and read pre-filled value from static hero input
  useEffect(() => {
    const handleHeroZip = (e: Event) => {
      const customEvent = e as CustomEvent<{ zipCode: string }>;
      const zipCode = customEvent.detail?.zipCode;
      if (zipCode && zipCode.length === 5) {
        setMetadata((prev) => ({
          ...prev,
          household: {
            ...prev.household,
            zipCode: zipCode,
          },
        }));
        setWizardStep("householdBasics");
      }
    };

    window.addEventListener("hero-zip-submit", handleHeroZip);
    
    // Check if hero-zip-input already has a 5-digit ZIP code on mount
    const zipInput = document.getElementById("hero-zip-input") as HTMLInputElement | null;
    if (zipInput && zipInput.value.length === 5) {
      setMetadata((prev) => ({
        ...prev,
        household: {
          ...prev.household,
          zipCode: zipInput.value,
        },
      }));
      setWizardStep("householdBasics");
    }

    return () => {
      window.removeEventListener("hero-zip-submit", handleHeroZip);
    };
  }, []);

  const [startLeadMutation] =
    useMutation<PublicStartLeadResponse>(PUBLIC_START_LEAD);
  const [updateHouseholdMutation] = useMutation<PublicUpdateHouseholdResponse>(
    PUBLIC_UPDATE_HOUSEHOLD
  );
  const [confirmPlanMutation] =
    useMutation<PublicConfirmPlanResponse>(PUBLIC_CONFIRM_PLAN);
  const [checkConsentMutation] =
    useMutation<PublicCheckConsentResponse>(PUBLIC_CHECK_CONSENT);
  const [initIdentityMutation] =
    useMutation<PublicInitIdentityResponse>(PUBLIC_INIT_IDENTITY);
  const [verifyIdentityOtpMutation] =
    useMutation<PublicVerifyIdentityOtpResponse>(PUBLIC_VERIFY_IDENTITY_OTP);
  const [createQuoteMutation] =
    useMutation<PublicCreateQuoteResponse>(PUBLIC_CREATE_QUOTE);
  const [updateLeadStageMutation] =
    useMutation<PublicUpdateLeadStageResponse>(PUBLIC_UPDATE_LEAD_STAGE);

  const pageTitle = t(STEP_TITLE_KEYS[wizardStep]);

  const updateLeadStage = useCallback(
    async (nextStageId?: number | null, explicitLeadId?: string | null) => {
      const resolvedStageId =
        typeof nextStageId === "number" && Number.isFinite(nextStageId)
          ? nextStageId
          : null;
      const effectiveLeadId = explicitLeadId ?? leadId;
      if (!effectiveLeadId || !resolvedStageId) {
        return;
      }
      if (
        leadStageRef.current.leadId === effectiveLeadId &&
        leadStageRef.current.stageId === resolvedStageId
      ) {
        return;
      }
      try {
        await updateLeadStageMutation({
          variables: {
            token: publicToken,
            leadId: effectiveLeadId,
            stagePipelineId: resolvedStageId,
          },
        });
        leadStageRef.current = {
          leadId: effectiveLeadId,
          stageId: resolvedStageId,
        };
      } catch (error) {
        console.error("Failed to update lead stage", error);
      }
    },
    [leadId, publicToken, updateLeadStageMutation]
  );

  useEffect(() => {
    pushToDataLayer({
      event: "wizard_step",
      step: wizardStep,
      language,
      ...(leadId ? { leadId } : {}),
      ...(leadId ? { lead_id: leadId } : {}),
    });
  }, [wizardStep, language, leadId]);

  useEffect(() => {
    if (!leadId) {
      consentTrackedLeadRef.current = null;
      return;
    }
    if (
      consentTrackedLeadRef.current &&
      consentTrackedLeadRef.current !== leadId &&
      consentStatus !== "SIGNED"
    ) {
      consentTrackedLeadRef.current = null;
    }
  }, [leadId, consentStatus]);

  useEffect(() => {
    if (consentStatus !== "SIGNED" || !leadId) {
      return;
    }
    if (consentTrackedLeadRef.current === leadId) {
      return;
    }

    const baseHolder = buildQuoteHolderState(metadata, currentPrimary ?? null);
    setQuoteHolderState(baseHolder);
    setQuoteMembersState(buildQuoteMembersState(metadata));
    setQuotePaymentState(buildQuotePaymentState(baseHolder));
    setQuotePolicyId(null);
    setApiError(null);
    if (
      !["quoteHolder", "quoteMembers", "quotePayment", "quoteSuccess"].includes(
        wizardStep
      )
    ) {
      setWizardStep("quoteHolder");
    }

    consentTrackedLeadRef.current = leadId;

    const planId = selectedPlan?.id ?? metadata.plan?.selection?.planId ?? null;

    const consentEventName = leadConfig.consentTrackingEventName || "consent_signed";
    const normEmail = normalizeEmail(primaryApplicant?.email);
    const normPhone = normalizePhone(primaryApplicant?.phone);
    const zipCode = metadata.household.zipCode;
    const region = metadata.household.stateId;
    const city = metadata.household.countyName;

    const userData = normEmail || normPhone
      ? {
        user_data: {
          ...(normEmail && { email: normEmail }),
          ...(normPhone && { phone_number: normPhone }),
          ...(primaryApplicant?.firstName && { first_name: primaryApplicant.firstName.trim() }),
          ...(primaryApplicant?.lastName && { last_name: primaryApplicant.lastName.trim() }),
          ...(primaryApplicant?.birthDate && { date_of_birth: primaryApplicant.birthDate }),
          country: "US", // Siempre "US" para Obamacare/ACA
          ...(zipCode && { postal_code: zipCode }),
          ...(region && { region: region.toUpperCase() }),
          ...(city && { city: city.trim() }),
        }
      }
      : {};

    if (consentEventName === "consent_signed") {
      // Unificar en un solo push a dataLayer para evitar gatillar etiquetas duplicadas en GTM
      pushToDataLayer({
        event: "consent_signed",
        leadId,
        lead_id: leadId,
        transaction_id: leadId,
        language,
        signedAt: new Date().toISOString(),
        ...(planId ? { planId } : {}),
        ...userData,
      });
    } else {
      // Si tienen nombres de evento distintos, mantenerlos separados
      trackFormSubmit(
        consentEventName,
        primaryApplicant?.email,
        primaryApplicant?.phone
      );
      pushToDataLayer({
        event: "consent_signed",
        leadId,
        lead_id: leadId,
        transaction_id: leadId,
        language,
        signedAt: new Date().toISOString(),
        ...(planId ? { planId } : {}),
      });
    }
    void updateLeadStage(
      leadConfig.stagePipelineSignedId ??
      leadConfig.stagePipelineId ??
      null
    );
  }, [
    consentStatus,
    leadId,
    selectedPlan,
    metadata,
    language,
    currentPrimary,
    wizardStep,
    primaryApplicant,
    leadConfig.stagePipelineSignedId,
    leadConfig.stagePipelineId,
    leadConfig.consentTrackingEventName,
    updateLeadStage,
  ]);

  useEffect(() => {
    if (identityVerified && wizardStep === "identityOtp") {
      setWizardStep("plans");
    }
  }, [identityVerified, wizardStep]);

  const handleToggleLanguage = () => {
    const nextLanguage: Language = language === "es" ? "en" : "es";
    setLanguage(nextLanguage);

    const currentHref = window.location.href;
    if (currentHref.includes(`/${language}`)) {
      window.location.href = currentHref.replace(`/${language}`, `/${nextLanguage}`);
    } else {
      window.location.reload();
    }
  };

  const handleSelectHouseholdType = (type: HouseholdType) => {
    setHouseholdType(type);
    setMetadata((prev) => ({
      ...prev,
      household: { ...prev.household, type },
    }));
    if (type !== undefined) {
      setWizardStep("householdBasics");
    }
  };

  const handleHouseholdBasicsSubmit = ({
    zipCode,
    income,
    countyFips,
    countyName,
    stateId,
    stateName,
    effectiveDate,
  }: {
    zipCode: string;
    income: number;
    countyFips: string;
    countyName?: string | null;
    stateId?: string | null;
    stateName?: string | null;
    effectiveDate?: string | null;
  }) => {
    setMetadata((prev) => ({
      ...prev,
      household: {
        ...prev.household,
        type: resolvedHouseholdType ?? householdType,
        zipCode,
        income,
        countyFips,
        countyName: countyName ?? prev.household.countyName,
        stateId: stateId ?? prev.household.stateId,
        stateName: stateName ?? prev.household.stateName,
        effectiveDate: effectiveDate ?? prev.household.effectiveDate,
      },
    }));
    setWizardStep("primary");
    setApiError(null);
  };

  const handlePrimarySubmit = async (applicant: WizardPrimaryApplicant) => {
    if (!resolvedHouseholdType) {
      return;
    }

    if (
      !leadConfig.campusId ||
      !leadConfig.leadSourceId ||
      !leadConfig.stagePipelineId
    ) {
      setApiError(
        "Missing lead configuration. Configure environment variables for stage pipeline, lead source and campus."
      );
      return;
    }

    const primaryMember = buildPrimaryMember(applicant);
    const existingAdditional = (metadata.members || []).filter(
      (member) => member.role !== "PRIMARY"
    );
    const nextMembers = [primaryMember, ...existingAdditional];

    const householdPayload: LeadHousehold = {
      ...metadata.household,
      type: resolvedHouseholdType,
    };

    setMetadata((prev) => ({
      ...prev,
      members: nextMembers,
      household: {
        ...prev.household,
        type: resolvedHouseholdType,
      },
    }));
    setPrimaryApplicant(applicant);
    setApiError(null);

    if (householdNeedsAdditionalMembers(resolvedHouseholdType) && !leadId) {
      setWizardStep("members");
      return;
    }

    const nextMetadata = await submitStartLead(
      nextMembers,
      applicant,
      householdPayload
    );

    if (!nextMetadata) {
      return;
    }

    const nextType =
      (nextMetadata.household.type as HouseholdType | null) ??
      resolvedHouseholdType;
    const needsMoreMembers = householdNeedsAdditionalMembers(nextType);
    if (needsMoreMembers) {
      setWizardStep("members");
      return;
    }

    const nextIdentityStatus = identityStatusFromMetadata(nextMetadata);
    if (!leadConfig.requireIdentity) {
      setWizardStep("plans");
    } else {
      setWizardStep(nextIdentityStatus === "VERIFIED" ? "plans" : "identityOtp");
    }

    if (nextIdentityStatus !== "VERIFIED") {
      setSigningLink(null);
      setConsentHtml(null);
    }
  };

  const handleMembersSubmit = async (membersList: LeadHouseholdMember[]) => {
    const normalizedMembers = membersList;
    setMetadata((prev) => ({
      ...prev,
      members: normalizedMembers,
    }));

    if (!leadId) {
      if (!primaryApplicant) {
        setApiError(t("wizard.errors.generic"));
        return;
      }

      const householdPayload: LeadHousehold = {
        ...metadata.household,
        type: resolvedHouseholdType,
      };

      const nextMetadata = await submitStartLead(
        normalizedMembers,
        primaryApplicant,
        householdPayload
      );

      if (nextMetadata) {
        const nextIdentityStatus = identityStatusFromMetadata(nextMetadata);
        if (!leadConfig.requireIdentity) {
          setWizardStep("plans");
        } else {
          setWizardStep(
            nextIdentityStatus === "VERIFIED" ? "plans" : "identityOtp"
          );
        }
        if (nextIdentityStatus !== "VERIFIED") {
          setSigningLink(null);
          setConsentHtml(null);
        }
      }
      return;
    }

    setIsSubmitting(true);
    setApiError(null);

    try {
      const { data } = await updateHouseholdMutation({
        variables: {
          token: publicToken,
          leadId,
          members: normalizedMembers.map(mapMemberToInput),
          wizardStep: "PLANS",
        },
      });

      const response = data?.publicUpdateHousehold;
      if (!response?.success) {
        const message =
          response?.errors?.[0]?.message || t("wizard.errors.generic");
        setApiError(message);
        return;
      }

      const nextMetadata = parseMetadata(response.metadata);
      setMetadata(nextMetadata);
      setConsentStatus(consentFromMetadata(nextMetadata));
      const nextIdentityStatus = identityStatusFromMetadata(nextMetadata);
      if (!leadConfig.requireIdentity) {
        setWizardStep("plans");
      } else {
        setWizardStep(
          nextIdentityStatus === "VERIFIED" ? "plans" : "identityOtp"
        );
      }
      if (nextIdentityStatus !== "VERIFIED") {
        setSigningLink(null);
        setConsentHtml(null);
      }
    } catch (error: any) {
      setApiError(error?.message ?? t("wizard.errors.generic"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePlanSelect = (plan: Plan, context: PlanSelectionContext) => {
    if (!isHouseholdComplete(resolvedHouseholdType, metadata.members ?? [])) {
      setApiError(t("wizard.members.errors.completeCouple"));
      setWizardStep("members");
      return;
    }
    if (!identityVerified && leadConfig.requireIdentity) {
      setApiError(t("wizard.errors.identityRequired"));
      setWizardStep("identityOtp");
      return;
    }

    setSelectedPlan(plan);
    setPlanContext(context);
    if (typeof plan.monthlyPremium === "number") {
      setRequiresPayment(plan.monthlyPremium > 0);
    }
    setWizardStep("summary");
  };

  const handleConfirmPlan = async () => {
    if (!leadId || !selectedPlan) {
      return;
    }

    if (!identityVerified && leadConfig.requireIdentity) {
      setApiError(t("wizard.errors.identityRequired"));
      setWizardStep("identityOtp");
      return;
    }

    if (consentStatus !== "NOT_REQUESTED" && signingLink) {
      setWizardStep("summary");
      return;
    }

    setIsSubmitting(true);
    setApiError(null);

    try {
      const { data } = await confirmPlanMutation({
        variables: {
          token: publicToken,
          leadId,
          planSelection: buildPlanSelectionInput(selectedPlan),
          planResults: {
            count: planContext?.count ?? 0,
            fetchedAt: planContext?.fetchedAt,
            filters: JSON.stringify({
              language,
              year: planContext?.year ?? undefined,
            }),
          },
          signatureFormId:
            language === "es"
              ? leadConfig.signatureFormId
              : leadConfig.signatureFormIdEn,
          agentId: leadConfig.agentId,
          sendEmail: leadConfig.sendEmail,
          sendSms: leadConfig.sendSms,
          getSigningLink: true,
        },
      });

      const response = data?.publicConfirmPlan;
      if (!response?.success) {
        const message =
          response?.errors?.[0]?.message || t("wizard.errors.generic");
        setApiError(message);
        return;
      }

      const nextMetadata = parseMetadata(response.metadata);
      setMetadata(nextMetadata);
      const nextConsentStatus = consentFromMetadata(nextMetadata);
      setConsentStatus(nextConsentStatus);
      setSigningLink(response.signingLink ?? null);
      setConsentHtml(response.consentHtml ?? null);
      setWizardStep(nextConsentStatus === "SIGNED" ? "consent" : "summary");
    } catch (error: any) {
      setApiError(error?.message ?? t("wizard.errors.generic"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckConsent = useCallback(
    async (silent = false) => {
      if (!leadId) {
        return;
      }
      if (!silent) {
        setIsCheckingConsent(true);
        setApiError(null);
      }

      try {
        const { data } = await checkConsentMutation({
          variables: {
            token: publicToken,
            leadId,
          },
        });

        const response = data?.publicCheckConsent;
        if (!response?.success) {
          if (!silent) {
            const message =
              response?.errors?.[0]?.message || t("wizard.errors.generic");
            setApiError(message);
          }
          return;
        }

        const nextMetadata = parseMetadata(response.metadata);
        setMetadata(nextMetadata);
        const nextStatus = consentFromMetadata(nextMetadata);
        setConsentStatus(nextStatus);

        if (nextStatus === "SIGNED") {
          setWizardStep("consent");
        }
      } catch (error: any) {
        if (!silent) {
          setApiError(error?.message ?? t("wizard.errors.generic"));
        }
      } finally {
        if (!silent) {
          setIsCheckingConsent(false);
        }
      }
    },
    [checkConsentMutation, leadId, publicToken, t]
  );

  useEffect(() => {
    if (consentStatus !== "PENDING" || !signingLink || !leadId) {
      return;
    }

    void handleCheckConsent(true);
    const interval = setInterval(() => {
      void handleCheckConsent(true);
    }, 7000);

    return () => clearInterval(interval);
  }, [consentStatus, signingLink, leadId, handleCheckConsent]);

  const handleRequestIdentityOtp = async (channels: {
    sendSms: boolean;
    sendEmail: boolean;
  }) => {
    if (!leadId) {
      throw new Error(t("wizard.errors.generic"));
    }

    setIsIdentityOtpSending(true);
    setApiError(null);

    try {
      const { data } = await initIdentityMutation({
        variables: {
          token: publicToken,
          leadId,
          sendSms: channels.sendSms,
          sendEmail: channels.sendEmail,
          language,
        },
      });

      const response = data?.publicInitIdentity;
      if (!response?.success || !response.identityToken) {
        const message =
          response?.errors?.[0]?.message || t("wizard.errors.generic");
        setApiError(message);
        throw new Error(message);
      }

      setIdentityToken(response.identityToken);
      setIdentityOtpStatus({
        sent: true,
        smsSent: Boolean(response.smsSent),
        emailSent: Boolean(response.emailSent),
      });

      if (response.metadata) {
        setMetadata(parseMetadata(response.metadata));
      }

      pushToDataLayer({
        event: "identity_otp_sent",
        leadId,
        lead_id: leadId,
        language,
        smsSent: Boolean(response.smsSent),
        emailSent: Boolean(response.emailSent),
      });
    } catch (error: any) {
      const message = error?.message ?? t("wizard.errors.generic");
      setApiError(message);
      throw new Error(message);
    } finally {
      setIsIdentityOtpSending(false);
    }
  };

  const handleVerifyIdentityOtp = async (code: string) => {
    if (!leadId || !identityToken) {
      throw new Error(t("identity.otp.codeRequired"));
    }

    setIsIdentityOtpVerifying(true);
    setApiError(null);

    try {
      const { data } = await verifyIdentityOtpMutation({
        variables: {
          token: publicToken,
          leadId,
          identityToken,
          otpCode: code,
        },
      });

      const response = data?.publicVerifyIdentityOtp;
      if (!response?.success) {
        const message =
          response?.errors?.[0]?.message || t("wizard.errors.generic");
        setApiError(message);
        throw new Error(message);
      }

      if (response.metadata) {
        const nextMetadata = parseMetadata(response.metadata);
        setMetadata(nextMetadata);
        setConsentStatus(consentFromMetadata(nextMetadata));
      }

      setIdentityToken(null);
      setIdentityOtpStatus((prev) => ({ ...prev, sent: true }));
      setSigningLink(null);
      setConsentHtml(null);
      setWizardStep("plans");

      pushToDataLayer({
        event: "identity_verified",
        leadId,
        lead_id: leadId,
        language,
      });
    } catch (error: any) {
      const message = error?.message ?? t("wizard.errors.generic");
      setApiError(message);
      throw new Error(message);
    } finally {
      setIsIdentityOtpVerifying(false);
    }
  };

  const handleQuoteHolderSubmit = async (values: QuoteHolderFormState) => {
    setApiError(null);
    setQuoteHolderState(values);
    setQuotePaymentState((prev) => ({
      ...prev,
      paymentHolder: `${values.firstName} ${values.lastName}`.trim(),
      billingAddress1: values.address1,
      billingAddress2: values.address2,
      billingCity: values.city,
      billingStateCode: values.stateCode,
      billingZipCode: values.zipCode,
    }));

    if (quoteMembersState.length > 0) {
      setWizardStep("quoteMembers");
      return;
    }

    if (requiresPayment) {
      setWizardStep("quotePayment");
      return;
    }

    const noPaymentValues = buildNoPaymentState(values);
    setQuotePaymentState(noPaymentValues);

    try {
      await handleQuotePaymentSubmit(noPaymentValues, quoteMembersState);
    } catch (error: any) {
      setApiError(error?.message ?? t("wizard.errors.generic"));
    }
  };

  const handleQuoteMembersSubmit = async (
    membersValues: QuoteMemberFormState[]
  ) => {
    setApiError(null);
    setQuoteMembersState(membersValues);
    if (requiresPayment) {
      setWizardStep("quotePayment");
      return;
    }

    const noPaymentValues = buildNoPaymentState(quoteHolderState);
    setQuotePaymentState(noPaymentValues);

    try {
      await handleQuotePaymentSubmit(noPaymentValues, membersValues);
    } catch (error: any) {
      setApiError(error?.message ?? t("wizard.errors.generic"));
    }
  };

  const handleQuotePaymentSubmit = async (
    values: QuotePaymentFormState,
    membersOverride?: QuoteMemberFormState[]
  ) => {
    if (!leadId) {
      setApiError(t("wizard.errors.generic"));
      return;
    }

    setQuotePaymentState(values);
    setIsCreatingQuote(true);
    setApiError(null);

    try {
      console.log("DEBUG: quoteHolderState =", quoteHolderState);
      const holderPayload = {
        firstName: quoteHolderState.firstName,
        lastName: quoteHolderState.lastName,
        gender: quoteHolderState.gender,
        birthDate: quoteHolderState.birthDate,
        email: quoteHolderState.email,
        phone: quoteHolderState.phone,
        address1: quoteHolderState.address1,
        address2: quoteHolderState.address2,
        city: quoteHolderState.city,
        stateCode: quoteHolderState.stateCode,
        stateName: quoteHolderState.stateName ?? "",
        zipCode: quoteHolderState.zipCode,
        countyFips: quoteHolderState.countyFips ?? "",
        ssn: quoteHolderState.ssn,
        usesTobacco: Boolean(quoteHolderState.usesTobacco),
        isPregnant: Boolean(quoteHolderState.isPregnant),
      };

      const membersSource = membersOverride ?? quoteMembersState;
      const membersPayload = membersSource.map((member) => ({
        firstName: member.firstName,
        lastName: member.lastName,
        gender: member.gender,
        birthDate: member.birthDate,
        relationship: member.relationship,
        email: member.email ?? "",
        phone: member.phone ?? "",
        usesTobacco: Boolean(member.usesTobacco),
        isPregnant: Boolean(member.isPregnant),
      }));

      const paymentMethod = values.providePaymentLater ? "NONE" : values.method;
      const paymentPayload = {
        method: paymentMethod,
        cardNumber:
          paymentMethod === "CARD" ? values.cardNumber.replace(/\D/g, "") : "",
        expirationMonth: paymentMethod === "CARD" ? values.expirationMonth : "",
        expirationYear: paymentMethod === "CARD" ? values.expirationYear : "",
        cvv: paymentMethod === "CARD" ? values.cvv : "",
        cardType: paymentMethod === "CARD" ? values.cardType : "",
        cardCompany: paymentMethod === "CARD" ? values.cardCompany : "",
        bankName: paymentMethod === "BANK" ? values.bankName : "",
        abaRouting: paymentMethod === "BANK" ? values.abaRouting : "",
        accountNumber: paymentMethod === "BANK" ? values.accountNumber : "",
        paymentHolder: values.paymentHolder,
        billingAddress1: values.billingAddress1,
        billingAddress2: values.billingAddress2,
        billingCity: values.billingCity,
        billingStateCode: values.billingStateCode,
        billingZipCode: values.billingZipCode,
      };

      const { data } = await createQuoteMutation({
        variables: {
          token: publicToken,
          leadId,
          quoteToken: null,
          holder: holderPayload,
          members: membersPayload,
          payment: paymentPayload,
        },
      });

      const response = data?.publicCreateQuote;
      if (!response?.success || !response.policyId) {
        const message =
          response?.errors?.[0]?.message || t("wizard.errors.generic");
        setApiError(message);
        return;
      }

      setQuotePolicyId(response.policyId);
      if (response.metadata) {
        setMetadata(parseMetadata(response.metadata));
      }
      setWizardStep("quoteSuccess");
      pushToDataLayer({
        event: "quote_created",
        leadId,
        lead_id: leadId,
        language,
        policyId: response.policyId,
        transaction_id: response.policyId || leadId,
      });
      pushToDataLayer({
        event: "quote_success_finish",
        language,
        ...(leadId ? { leadId } : {}),
        ...(leadId ? { lead_id: leadId } : {}),
        policyId: response.policyId,
        transaction_id: response.policyId || leadId,
        ...(successPlanName ? { planName: successPlanName } : {}),
        ...(successPlanCarrier ? { planCarrier: successPlanCarrier } : {}),
      });
      void updateLeadStage(
        leadConfig.stagePipelineQuotedId ??
        leadConfig.stagePipelineId ??
        null
      );
    } catch (error: any) {
      const message = error?.message ?? t("wizard.errors.generic");
      setApiError(message);
    } finally {
      setIsCreatingQuote(false);
    }
  };

  const handleQuoteFinish = useCallback(() => {
    window.location.reload();
  }, []);

  const nextLanguage: Language = language === "es" ? "en" : "es";
  const toggleLabel = t("header.languageToggleLabel", {
    language: t(`header.options.${nextLanguage}`),
  });

  const wizardSteps = useMemo<WizardStep[]>(() => {
    const steps: WizardStep[] = ["householdType", "householdBasics", "primary"];

    if (householdNeedsAdditionalMembers(resolvedHouseholdType)) {
      steps.push("members");
    }

    if (leadConfig.requireIdentity) {
      steps.push("identityOtp");
    }

    steps.push("plans", "summary", "consent", "quoteHolder");

    if (quoteMembersState.length > 0) {
      steps.push("quoteMembers");
    }

    if (requiresPayment) {
      steps.push("quotePayment");
    }

    steps.push("quoteSuccess");

    return steps;
  }, [resolvedHouseholdType, quoteMembersState.length, requiresPayment]);

  const currentStepIndex = wizardSteps.indexOf(wizardStep);
  const progress =
    currentStepIndex >= 0
      ? ((currentStepIndex + 1) / wizardSteps.length) * 100
      : 0;

  const selectedHouseholdTypeIcon = useMemo(() => {
    const match = OPTIONS.find((option) => option.type === householdType);
    return match?.icon ?? null;
  }, [householdType]);

  // Wizard phases para el stepper visual (agrupadas)
  const WIZARD_PHASES = [
    { key: "household", steps: ["householdType", "householdBasics"] as WizardStep[], label: t("steps.household") },
    { key: "primary", steps: ["primary", "members"] as WizardStep[], label: t("wizard.titles.primary") },
    { key: "identity", steps: ["identityOtp"] as WizardStep[], label: t("wizard.titles.identityOtp") },
    { key: "plans", steps: ["plans", "summary", "consent"] as WizardStep[], label: t("steps.plans") },
    { key: "quote", steps: ["quoteHolder", "quoteMembers", "quotePayment", "quoteSuccess"] as WizardStep[], label: t("steps.details") },
  ];

  const visiblePhases = WIZARD_PHASES.filter(phase =>
    phase.steps.some(s => wizardSteps.includes(s))
  );

  const currentPhaseIndex = visiblePhases.findIndex(phase =>
    phase.steps.includes(wizardStep)
  );

  const stepperFillPct = visiblePhases.length > 1
    ? (currentPhaseIndex / (visiblePhases.length - 1)) * 100
    : 0;

  const handleHeroZipChange = async (value: string) => {
    const numberOnly = value.replace(/\D/g, "");
    setHeroZip(numberOnly);

    if (numberOnly.length >= 3) {
      try {
        const { data } = await fetchZipcodeSuggestions({
          variables: {
            token: publicToken,
            prefix: numberOnly,
            limit: 5,
          },
        });
        const suggestions = data?.publicZipcodeSuggestions ?? [];
        setHeroZipSuggestions(suggestions);
        setShowHeroSuggestions(suggestions.length > 0);
      } catch (error) {
        console.warn("[hero] zip suggestions failed", error);
      }
    } else {
      setHeroZipSuggestions([]);
      setShowHeroSuggestions(false);
    }
  };

  const handleHeroSuggestionSelect = async (suggestion: ZipcodeSuggestion) => {
    setHeroZip(suggestion.zipCode);
    setShowHeroSuggestions(false);

    try {
      const { data } = await fetchZipcode({
        variables: { token: publicToken, zipCode: suggestion.zipCode },
      });
      const record = data?.publicZipcodeByZip;
      if (record) {
        setMetadata((prev) => ({
          ...prev,
          household: {
            ...prev.household,
            zipCode: record.zipCode,
            stateId: record.stateId,
            stateName: record.stateName,
            countyFips: record.countyFips ?? "",
            countyName: record.countyName ?? "",
          },
        }));
      }
    } catch (error) {
      console.warn("[hero] fetch zip info failed", error);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        heroSuggestionsRef.current &&
        !heroSuggestionsRef.current.contains(event.target as Node)
      ) {
        setShowHeroSuggestions(false);
      }
    };
    if (showHeroSuggestions) {
      document.addEventListener("click", handleOutsideClick);
    }
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [showHeroSuggestions]);

  const handleScrollToWizard = useCallback((e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const wizardTarget =
      document.querySelector("#quote-wizard") ||
      document.querySelector(".wizard-progress");
    if (wizardTarget) {
      wizardTarget.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const heroTitle =
    language === "es"
      ? "Tu Cotizacion Gratis En Tan Solo Unos Clicks"
      : "Your Free Quote In Just A Few Clicks";
  const heroSubtitle =
    language === "es"
      ? "Planes de Seguro de Salud De Obamacare y Privados"
      : "Obamacare & Private Health Insurance Plans";
  const heroPriceLine =
    language === "es"
      ? "Desde $0 al Mes *Previa Calificacion"
      : "From $0/Month *Subject to Qualification";
  const socialCards =
    language === "es"
      ? [
        {
          id: "views",
          icon: <svg className="social-icon green" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>,
          boldText: "75 personas",
          regularText: "buscando ahora"
        },
        {
          id: "families",
          icon: <svg className="social-icon blue" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>,
          boldText: "+1M",
          regularText: "familias ayudadas"
        },
        {
          id: "recent",
          icon: <svg className="social-icon orange" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>,
          boldText: RECENT_CITIES[currentCityIndex],
          regularText: "Reciente:",
          reverse: true
        },
      ]
      : [
        {
          id: "views",
          icon: <svg className="social-icon green" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>,
          boldText: "75 people",
          regularText: "searching now"
        },
        {
          id: "families",
          icon: <svg className="social-icon blue" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>,
          boldText: "+1M",
          regularText: "families helped"
        },
        {
          id: "recent",
          icon: <svg className="social-icon orange" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>,
          boldText: RECENT_CITIES[currentCityIndex],
          regularText: "Recent:",
          reverse: true
        },
      ];
  const riskCards =
    language === "es"
      ? [
        {
          title: "Emergencia Medica",
          text: "Sin seguro, una visita a emergencias puede costarte miles de dolares.",
        },
        {
          title: "Costos Inesperados",
          text: "Un procedimiento medico sin cobertura puede generar facturas enormes.",
        },
        {
          title: "Deudas Medicas",
          text: "Las deudas medicas pueden afectar tu historial y desestabilizar tu hogar.",
        },
      ]
      : [
        {
          title: "Medical Emergency",
          text: "Without insurance, an ER visit can cost thousands of dollars.",
        },
        {
          title: "Unexpected Costs",
          text: "A medical procedure without coverage can generate very large bills.",
        },
        {
          title: "Medical Debt",
          text: "Medical debt can damage your credit and household stability.",
        },
      ];
  const premiumBenefits =
    language === "es"
      ? [
        { title: "Comenzar toma menos de 5 minutos", text: "Proceso rápido y sencillo" },
        { title: "Nuestros asesores expertos te ayudarán", text: "A elegir la cobertura correcta" },
        { title: "Compara planes para encontrar", text: "Las tarifas más bajas disponibles" },
      ]
      : [
        { title: "Getting started takes less than 5 minutes", text: "Quick and simple process" },
        { title: "Our expert advisors will help you", text: "To choose the right coverage" },
        { title: "Compare plans to find", text: "The lowest available rates" },
      ];

  const fplTableHeaders =
    language === "es"
      ? ["Tamaño del Hogar", "100% FPL", "150% FPL", "200% FPL ★", "400% FPL"]
      : ["Household Size", "100% FPL", "150% FPL", "200% FPL ★", "400% FPL"];

  const fplTableRows =
    language === "es"
      ? [
        { size: "1 persona", fpl100: "$15,060", fpl150: "$22,590", fpl200: "$30,120", fpl400: "$60,240" },
        { size: "2 personas", fpl100: "$20,440", fpl150: "$30,660", fpl200: "$40,880", fpl400: "$81,760" },
        { size: "3 personas", fpl100: "$25,820", fpl150: "$38,730", fpl200: "$51,640", fpl400: "$103,280" },
        { size: "4 personas", fpl100: "$31,200", fpl150: "$46,800", fpl200: "$62,400", fpl400: "$124,800" },
        { size: "5 personas", fpl100: "$36,580", fpl150: "$54,870", fpl200: "$73,160", fpl400: "$146,320" },
        { size: "6 personas", fpl100: "$41,960", fpl150: "$62,940", fpl200: "$83,920", fpl400: "$167,840" },
      ]
      : [
        { size: "1 person", fpl100: "$15,060", fpl150: "$22,590", fpl200: "$30,120", fpl400: "$60,240" },
        { size: "2 people", fpl100: "$20,440", fpl150: "$30,660", fpl200: "$40,880", fpl400: "$81,760" },
        { size: "3 people", fpl100: "$25,820", fpl150: "$38,730", fpl200: "$51,640", fpl400: "$103,280" },
        { size: "4 people", fpl100: "$31,200", fpl150: "$46,800", fpl200: "$62,400", fpl400: "$124,800" },
        { size: "5 people", fpl100: "$36,580", fpl150: "$54,870", fpl200: "$73,160", fpl400: "$146,320" },
        { size: "6 people", fpl100: "$41,960", fpl150: "$62,940", fpl200: "$83,920", fpl400: "$167,840" },
      ];

  const detailBenefits =
    language === "es"
      ? [
        "Servicios de emergencia",
        "Servicios para pacientes ambulatorios",
        "Hospitalizacion",
        "Cuidado antes y despues que nazca su bebe",
        "Servicios para la salud mental y abuso de sustancias",
        "Sus medicamentos recetados",
        "Fisioterapia, terapia ocupacional y rehabilitacion",
        "Servicios de laboratorios",
        "Servicios preventivos: examenes, vacunas y consejeria",
        "Servicios pediatricos: cuidado dental y de la vista",
      ]
      : [
        "Emergency services",
        "Outpatient services",
        "Hospitalization",
        "Maternity and newborn care",
        "Mental health and substance abuse services",
        "Prescription medications",
        "Physical therapy, occupational therapy and rehabilitation",
        "Laboratory services",
        "Preventive services: screenings, vaccines and counseling",
        "Pediatric services: dental and vision care",
      ];
  const metalTiers =
    language === "es"
      ? [
        { id: "bronze", name: "Bronce", planPays: "60%", youPay: "40%" },
        { id: "silver", name: "Plata", planPays: "70%", youPay: "30%" },
        { id: "gold", name: "Oro", planPays: "80%", youPay: "20%" },
        { id: "platinum", name: "Platino", planPays: "90%", youPay: "10%" },
      ]
      : [
        { id: "bronze", name: "Bronze", planPays: "60%", youPay: "40%" },
        { id: "silver", name: "Silver", planPays: "70%", youPay: "30%" },
        { id: "gold", name: "Gold", planPays: "80%", youPay: "20%" },
        { id: "platinum", name: "Platinum", planPays: "90%", youPay: "10%" },
      ];
  const testimonials =
    language === "es"
      ? [
        {
          img: "/media/testimonial-maria.jpg",
          quote:
            "Estoy encantada con el servicio. Mi agente me encontro un plan increible con $0 de prima mensual.",
          name: "Maria G.",
          city: "Miami, FL",
        },
        {
          img: "/media/testimonial-carlos.jpg",
          quote:
            "El proceso fue super facil. En menos de 10 minutos ya tenia mi plan de salud para toda la familia.",
          name: "Carlos R.",
          city: "Houston, TX",
        },
        {
          img: "/media/testimonial-rosa.jpg",
          quote:
            "Me ayudaron a conseguir cobertura completa por muy poco. Gracias por la paciencia y claridad.",
          name: "Rosa M.",
          city: "Los Angeles, CA",
        },
      ]
      : [
        {
          img: "/media/testimonial-maria.jpg",
          quote:
            "I was thrilled with the service. My agent found me an amazing plan with a $0 monthly premium.",
          name: "Maria G.",
          city: "Miami, FL",
        },
        {
          img: "/media/testimonial-carlos.jpg",
          quote:
            "The process was super easy. In less than 10 minutes I had a health plan for the whole family.",
          name: "Carlos R.",
          city: "Houston, TX",
        },
        {
          img: "/media/testimonial-rosa.jpg",
          quote:
            "They helped me get full coverage for very little. Thanks for the patience and clarity.",
          name: "Rosa M.",
          city: "Los Angeles, CA",
        },
      ];
  const stepsContent =
    language === "es"
      ? [
        {
          number: "1",
          title: "Cuentanos sobre ti",
          text: "Completa el formulario con tu codigo postal e informacion basica.",
        },
        {
          number: "2",
          title: "Comparamos planes",
          text: "Un agente licenciado analiza las mejores opciones segun tus necesidades e ingresos.",
        },
        {
          number: "3",
          title: "Queda protegido",
          text: "Selecciona tu plan ideal y completa la inscripcion rapido y sin costo.",
        },
      ]
      : [
        {
          number: "1",
          title: "Tell us about yourself",
          text: "Complete the form with your ZIP code and basic information.",
        },
        {
          number: "2",
          title: "We compare plans",
          text: "A licensed agent reviews the best options based on your needs and income.",
        },
        {
          number: "3",
          title: "Get protected",
          text: "Choose your ideal plan and complete enrollment quickly and at no cost.",
        },
      ];
  const faqItems =
    language === "es"
      ? [
        {
          q: "¿Cuánto cuesta inscribirse?",
          a: "La inscripción es 100% gratuita. Solo pagarás la prima mensual del plan que elijas. Muchas personas califican para subsidios que reducen el costo a $0/mes.",
        },
        {
          q: "¿Puedo mantener a mi médico actual?",
          a: "Depende del plan que elijas. Cada plan tiene su propia red de proveedores. Nuestros agentes te ayudan a verificar que tu médico esté incluido antes de inscribirte.",
        },
        {
          q: "¿Califico para subsidios o ayuda financiera?",
          a: "Depende de tus ingresos y tamaño de familia. Nuestros agentes verifican automáticamente si calificas durante la llamada. La mayoría de nuestros clientes reciben algún tipo de ayuda.",
        },
        {
          q: "¿Qué documentos necesito?",
          a: "Necesitarás tu número de Seguro Social, información de ingresos (W-2 o declaración de impuestos) e información de los miembros de tu familia que deseas cubrir.",
        },
        {
          q: "¿Cuándo es el período de inscripción?",
          a: "La inscripción abierta es generalmente del 1 de noviembre al 15 de enero. Si tienes un evento de vida calificador (pérdida de empleo, matrimonio, nacimiento), puedes inscribirte en cualquier momento.",
        },
        {
          q: "¿Necesito ser ciudadano para inscribirme?",
          a: "No necesitas ser ciudadano. Residentes permanentes legales y personas con ciertos estatus migratorios también pueden calificar para planes del Mercado de Salud.",
        },
      ]
      : [
        {
          q: "How much does it cost to enroll?",
          a: "Enrollment is 100% free. You'll only pay the monthly premium of the plan you choose. Many people qualify for subsidies that lower the cost to $0/month.",
        },
        {
          q: "Can I keep my current doctor?",
          a: "It depends on the plan you choose. Each plan has its own provider network. Our agents help verify that your doctor is included before enrolling.",
        },
        {
          q: "Do I qualify for subsidies or financial help?",
          a: "It depends on your income and household size. Our agents automatically check if you qualify during the call. Most of our clients receive some form of assistance.",
        },
        {
          q: "What documents do I need?",
          a: "You'll need your Social Security number, income information (W-2 or tax return), and information for the household members you want to cover.",
        },
        {
          q: "When is the enrollment period?",
          a: "Open enrollment is generally from November 1 to January 15. If you have a qualifying life event (job loss, marriage, birth), you can enroll at any time.",
        },
        {
          q: "Do I need to be a citizen to enroll?",
          a: "You do not need to be a citizen. Lawful permanent residents and individuals with certain immigration statuses can also qualify for Marketplace health plans.",
        },
      ];

  if (isStaticMode) {
    return (
      <>
        <div className="health-section-header centered">
          <h2>
            {language === "es" ? "Obten tu " : "Get your "}
            <span>{language === "es" ? "Cotizacion Gratis" : "Free Quote"}</span>
          </h2>
          <p>
            {language === "es"
              ? "Completa los pasos y recibe acompanamiento para encontrar el mejor plan."
              : "Complete the steps and get support finding the best plan."}
          </p>
        </div>
        {apiError && <div className="form-error major-error">{apiError}</div>}
        {/* Stepper visual de fases */}
        {wizardStep !== "quoteSuccess" && (
          <div className="wizard-progress">
            <div className="wizard-stepper" role="progressbar" aria-label="Progreso del formulario">
              <div
                className="wizard-stepper-fill"
                style={{ width: `${stepperFillPct}%` }}
              />
              {visiblePhases.map((phase, idx) => {
                const isDone = idx < currentPhaseIndex;
                const isActive = idx === currentPhaseIndex;
                return (
                  <div
                    key={phase.key}
                    className={`wizard-phase${isDone ? " done" : isActive ? " active" : ""}`}
                  >
                    <div className={`wizard-phase-dot${isDone ? " done" : isActive ? " active" : ""}`}>
                      {isDone ? "✓" : idx + 1}
                    </div>
                    <div className="wizard-phase-name">{phase.label}</div>
                  </div>
                );
              })}
            </div>
            {currentPhaseIndex >= 0 && (
              <div className="wizard-step-counter">
                {t("wizard.stepCounter", {
                  current: String(currentPhaseIndex + 1),
                  total: String(visiblePhases.length),
                  phase: visiblePhases[currentPhaseIndex]?.label ?? "",
                })}
              </div>
            )}
          </div>
        )}
        <div className="card-wizard">
          <section className="page-heading">
            <div>
              <h2>{pageTitle}</h2>
            </div>
          </section>
          {wizardStep === "householdType" && (
            <HouseholdTypeStep
              value={householdType}
              onSelect={handleSelectHouseholdType}
            />
          )}

          {wizardStep === "householdBasics" && householdType && (
            <HouseholdBasicsStep
              householdType={householdType}
              initialZip={metadata.household.zipCode}
              selectedHouseholdTypeIcon={selectedHouseholdTypeIcon}
              initialIncome={metadata.household.income}
              initialCountyFips={metadata.household.countyFips}
              initialEffectiveDate={metadata.household.effectiveDate}
              onBack={() => setWizardStep("householdType")}
              onSubmit={handleHouseholdBasicsSubmit}
            />
          )}

          {wizardStep === "primary" && householdType && (
            <PrimaryApplicantStep
              selectedHouseholdTypeIcon={selectedHouseholdTypeIcon}
              householdType={householdType}
              initialValues={currentPrimary}
              onBack={() => setWizardStep("householdBasics")}
              onSubmit={handlePrimarySubmit}
            />
          )}

          {wizardStep === "members" && householdType && (
            <AdditionalMembersStep
              selectedHouseholdTypeIcon={selectedHouseholdTypeIcon}
              householdType={householdType}
              members={metadata.members}
              onBack={() => setWizardStep("primary")}
              onSubmit={handleMembersSubmit}
            />
          )}

          {wizardStep === "identityOtp" && (
            <QuoteOtpStep
              onSend={handleRequestIdentityOtp}
              onVerify={handleVerifyIdentityOtp}
              isSending={isIdentityOtpSending}
              isVerifying={isIdentityOtpVerifying}
              otpSent={identityOtpStatus.sent}
              smsSent={identityOtpStatus.smsSent}
              emailSent={identityOtpStatus.emailSent}
              error={apiError}
              leadEmail={currentPrimary?.email ?? null}
              leadPhone={currentPrimary?.phone ?? null}
              translationPrefix="identity.otp"
            />
          )}

          {wizardStep === "plans" && currentPrimary && (
            <>
              <PlanSelectionPage
                metadata={metadata}
                onBack={() =>
                  householdNeedsAdditionalMembers(householdType)
                    ? setWizardStep("members")
                    : setWizardStep("primary")
                }
                onPlanSelect={handlePlanSelect}
              />
              <div className="social-proof-bar" style={{ marginBottom: "24px" }}>
                <div className="social-proof-item">
                  <span className="social-proof-icon">🔒</span>
                  <span>
                    {language === "es" ? "Gratis y sin compromiso" : "Free & no commitment"}
                  </span>
                </div>
                <div className="social-proof-divider" />
                <div className="social-proof-item">
                  <span className="social-proof-icon">🛡️</span>
                  <span>
                    {language === "es" ? "256-bit encriptado" : "256-bit encrypted"}
                  </span>
                </div>
                <div className="social-proof-divider" />
                <div className="social-proof-item">
                  <span className="social-proof-icon">✅</span>
                  <span>
                    {language === "es" ? "Agente licenciado ACA" : "Licensed ACA agent"}
                  </span>
                </div>
              </div>
            </>
          )}

          {wizardStep === "summary" && selectedPlan && currentPrimary && (
            <PlanSummaryPage
              metadata={metadata}
              plan={selectedPlan}
              primary={currentPrimary}
              consentStatus={consentStatus}
              language={language}
              signingLink={signingLink}
              consentHtml={consentHtml}
              onBack={() => setWizardStep("plans")}
              onConfirm={handleConfirmPlan}
              onCheckConsent={
                consentStatus === "SIGNED"
                  ? undefined
                  : () => handleCheckConsent(false)
              }
              isSubmitting={isSubmitting}
              isCheckingConsent={isCheckingConsent}
            />
          )}

          {wizardStep === "consent" && selectedPlan && currentPrimary && (
            <PlanSummaryPage
              metadata={metadata}
              plan={selectedPlan}
              primary={currentPrimary}
              consentStatus={consentStatus}
              signingLink={signingLink}
              consentHtml={consentHtml}
              onBack={() => setWizardStep("summary")}
              onConfirm={() => { }}
              onCheckConsent={() => handleCheckConsent(false)}
              isSubmitting={false}
              isCheckingConsent={isCheckingConsent}
            />
          )}

          {wizardStep === "quoteHolder" && (
            <QuoteHolderStep
              selectedHouseholdTypeIcon={selectedHouseholdTypeIcon}
              initialValues={quoteHolderState}
              onSubmit={handleQuoteHolderSubmit}
              onBack={() => setWizardStep("consent")}
              isSubmitting={false}
            />
          )}

          {wizardStep === "quoteMembers" && (
            <QuoteMembersStep
              selectedHouseholdTypeIcon={selectedHouseholdTypeIcon}
              initialMembers={quoteMembersState}
              onSubmit={handleQuoteMembersSubmit}
              onBack={() => setWizardStep("quoteHolder")}
              isSubmitting={false}
            />
          )}

          {wizardStep === "quotePayment" && (
            <QuotePaymentStep
              initialValues={quotePaymentState}
              onSubmit={handleQuotePaymentSubmit}
              onBack={() =>
                quoteMembersState.length > 0
                  ? setWizardStep("quoteMembers")
                  : setWizardStep("quoteHolder")
              }
              isSubmitting={isCreatingQuote}
              error={apiError}
            />
          )}

          {wizardStep === "quoteSuccess" && (
            <QuoteSuccessStep
              policyId={quotePolicyId}
              selectedHouseholdTypeIcon={selectedHouseholdTypeIcon}
              planName={successPlanName}
              planCarrier={successPlanCarrier}
              planPremium={
                successPlanPremium !== null &&
                  Number.isFinite(successPlanPremium)
                  ? successPlanPremium
                  : null
              }
              onFinish={handleQuoteFinish}
            />
          )}
        </div>

        {isSubmitting && !["summary", "consent"].includes(wizardStep) && (
          <div className="loading-overlay">
            <Spinner animation="border" role="status" />
            <span>{t("wizard.loading")}</span>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="app-shell">
      {/* Floating WhatsApp Button */}
      <a
        href={`https://wa.me/${cleanWhatsApp}?text=${language === "es" ? "Hola,%20me%20interesa%20obtener%20una%20cotización%20de%20seguro%20de%20salud." : "Hello,%20I%20am%20interested%20in%20getting%20a%20health%20insurance%20quote."}`}
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
      >
        <svg className="whatsapp-icon" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
        </svg>
        <span>{language === "es" ? "Chat en Vivo" : "Live Chat"}</span>
      </a>

      {/* Sticky Mobile Call Bar */}
      <div className="sticky-call-bar">
        <a
          href={activePhoneHref}
          className="health-call-button"
          style={{ flex: 1, margin: 0 }}
          onClick={() => trackPhoneCall(activePhone, language, conversionIdRef.current)}
        >
          <FontAwesomeIcon icon={faPhone} />
          <span>{activePhone}</span>
        </a>
        <button type="button" className="health-cta-button" onClick={handleScrollToWizard} style={{ flex: 1, margin: 0 }}>
          {language === "es" ? "Cotizar" : "Quote"}
        </button>
      </div>

      {/* ── Announcement bar ── */}
      <div className="announcement-bar" onClick={handleScrollToWizard}>
        <div className="announcement-bar-inner">
          <div className="announcement-bar-pill">
            {language === "es"
              ? "🔥 Otros 75 clientes buscando ahora - No esperes"
              : "🔥 75 other clients searching now - Don't wait"}
          </div>
          <div className="announcement-bar-cta">
            {language === "es" ? "Cotizar Gratis" : "Get Free Quote"}
            <span>→</span>
          </div>
        </div>
      </div>

      <header className="app-header">
        <div className="app-header-inner">
          <div className="app-header-left">
            <div className="app-header-logo">
              <img
                className="app-logo"
                src="/media/tfi-logo.svg"
                width={155}
                height={56}
                alt="TFI logo"
              />
            </div>
            <div className="app-header-copy">
              <p className="app-subheadline">{t("header.subtitle")}</p>
            </div>
          </div>
          <div className="app-header-right">
            <div className="header-info-call">
              <span className="header-callus">{t("header.callus")}</span>
              <div className="btn-callus">
                <FontAwesomeIcon icon={faPhone} className="signing-link-icon" />
                <span className="header-phone">
                  <a href={activePhoneHref} onClick={() => trackPhoneCall(activePhone, language, conversionIdRef.current)}>
                    {activePhone}
                  </a>
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={handleToggleLanguage}
              className="language-toggle"
              aria-label={toggleLabel}
            >
              <img
                src={`/media/flags/${nextLanguage}.png`}
                alt={t(`header.options.${nextLanguage}`)}
                className="language-flag"
              />
              <span>{nextLanguage.toUpperCase()}</span>
            </button>
          </div>
        </div>
      </header>

      <section className="health-hero">
        <div className="health-container health-hero-grid">
          <div className="health-hero-copy">
            <p className="health-hero-disclaimer">
              {language === "es"
                ? "✓ Sitio independiente especializado en planes asequibles"
                : "✓ Independent site specialized in affordable plans"}
            </p>
            <h1 className="health-hero-title">
              {language === "es" ? "Cotización Gratis" : "Free Quote"}{" "}
              <span>{language === "es" ? "en Minutos" : "in Minutes"}</span>
            </h1>
            <p className="health-hero-subtitle">{heroSubtitle}</p>
            <p className="health-hero-price">{heroPriceLine}</p>

            <form onSubmit={handleScrollToWizard} className="health-hero-form">
              <div className="hero-zip-input-group" ref={heroSuggestionsRef}>
                <div className="hero-zip-field">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="hero-zip-icon" />
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={5}
                    placeholder={language === "es" ? "Ingresa tu ZIP code" : "Enter your ZIP code"}
                    className="hero-zip-input"
                    value={heroZip}
                    onChange={(e) => handleHeroZipChange(e.target.value)}
                    onFocus={() => {
                      if (heroZipSuggestions.length > 0) setShowHeroSuggestions(true);
                    }}
                  />
                  {showHeroSuggestions && heroZipSuggestions.length > 0 && (
                    <div className="hero-zip-suggestions">
                      {heroZipSuggestions.map((suggestion) => (
                        <button
                          type="button"
                          key={`${suggestion.zipCode}-${suggestion.city}`}
                          onClick={() => handleHeroSuggestionSelect(suggestion)}
                        >
                          <strong>{suggestion.zipCode}</strong>
                          <span>
                            {suggestion.city}, {suggestion.stateName}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button type="submit" className="hero-zip-submit">
                  {language === "es" ? "VERIFICAR MI ELEGIBILIDAD" : "CHECK MY ELIGIBILITY"}
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
              <p className="hero-zip-helper">
                {language === "es" ? "Toma menos de 2 minutos" : "Takes less than 2 minutes"}
              </p>
            </form>

            <div className="health-hero-bullets">
              {[
                language === "es" ? "Agentes Licenciados ACA" : "Licensed ACA Agents",
                language === "es" ? "0 Costo por Servicio" : "$0 Service Cost",
                language === "es" ? "Planes desde $0/mes" : "Plans from $0/month"
              ].map((item) => (
                <div key={item} className="hero-bullet-item">
                  <FontAwesomeIcon icon={faCheckCircle} className="hero-bullet-icon" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <div className="hero-progress-section">
              <div className="hero-progress-labels">
                <span>{language === "es" ? "4 de 5 personas califican para plan $0" : "4 out of 5 people qualify for a $0 plan"}</span>
                <span>80%</span>
              </div>
              <div className="hero-progress-bar">
                <div className="hero-progress-fill" />
              </div>
            </div>

            <div className="health-hero-proof">
              <FontAwesomeIcon icon={faCheckCircle} className="hero-bullet-icon" style={{ fontSize: '20px' }} />
              <span>
                {language === "es" ? "Hemos ayudado a " : "We've helped "}
                <strong>{language === "es" ? "mas de 1 millon de familias" : "over 1 million families"}</strong>
                {language === "es" ? " en EE.UU." : " in the U.S."}
              </span>
            </div>

            <div className="health-call-card">
              <div>
                <p className="health-call-label">
                  {language === "es" ? "Habla con un agente licenciado" : "Speak with a licensed agent"}
                </p>
                <a
                  className="health-call-phone"
                  href={activePhoneHref}
                  onClick={() => trackPhoneCall(activePhone, language, conversionIdRef.current)}
                >
                  {activePhone}
                </a>
                <p style={{ marginTop: '4px', fontSize: '12px', color: '#6B7280' }}>
                  {language === "es" ? "Disponible: Lun-Vie, 7 AM - 8 PM ET" : "Available: Mon-Fri, 7 AM - 8 PM ET"}
                </p>
              </div>
              <a
                className="health-call-button"
                href={activePhoneHref}
                onClick={() => trackPhoneCall(activePhone, language, conversionIdRef.current)}
              >
                <FontAwesomeIcon icon={faPhone} />
                <span>{language === "es" ? "Llamar Ahora" : "Call Now"}</span>
              </a>
            </div>
          </div>

          <div className="health-hero-media">
            <img src="/media/hero-family.jpg" alt="Happy family" />
            <div className="health-hero-badge">
              <div className="health-hero-badge-inner">
                <div className="health-hero-badge-icon-wrap">
                  <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '24px' }} />
                </div>
                <div className="health-hero-badge-text-wrap">
                  <strong>+1M</strong>
                  <span>{language === "es" ? "Familias protegidas este año" : "Families protected this year"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="health-social-proof">
        <div className="health-container health-social-grid">
          {socialCards.map((item) => (
            <div key={item.id} className="health-social-card">
              {item.icon}
              <div className="health-social-text">
                {item.reverse ? (
                  <>
                    <span>{item.regularText}</span>
                    <strong>{item.boldText}</strong>
                  </>
                ) : (
                  <>
                    <strong>{item.boldText}</strong>
                    <span>{item.regularText}</span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="health-carriers">
        <div className="health-container">
          <p className="health-section-caption">
            {language === "es"
              ? "Compara planes de los principales proveedores de seguros"
              : "Compare plans from top insurance providers"}
          </p>
          <div className="carrier-carousel-section" style={{ marginTop: 'auto' }}>
            <p className="carrier-carousel-title">{t("header.carrierCompareLabel")}</p>
            <div className="carrier-carousel-track">
              {[1, 2].map((set) => (
                <div className="carrier-carousel-track-inner" key={set} aria-hidden={set === 2}>
                  <div className="carrier-logo-card"><img src="/media/cigna.png" alt="Cigna" /></div>
                  <div className="carrier-logo-card"><img src="/media/aetna.png" alt="Aetna" /></div>
                  <div className="carrier-logo-card"><img src="/media/ambetter.png" alt="Ambetter" /></div>
                  <div className="carrier-logo-card"><img src="/media/UnitedHealthcare.png" alt="UnitedHealthcare" /></div>
                  <div className="carrier-logo-card"><img src="/media/molinahealthcare.jpg" alt="Molina Healthcare" /></div>
                  <div className="carrier-logo-card"><img src="/media/oscar.jpg" alt="Oscar" /></div>
                  <div className="carrier-logo-card"><img src="/media/BHC_HealthCare.png" alt="BHC_HealthCare" /></div>
                  <div className="carrier-logo-card"><img src="/media/bluecross.png" alt="bluecross" /></div>
                  <div className="carrier-logo-card"><img src="/media/humana.png" alt="humana" /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="health-section">
        <div className="health-container">
          <CoverageTypeStep />
        </div>
      </section>

      <section className="health-section">
        <div className="health-container">
          <div className="health-section-header">
            <h2>
              {language === "es" ? "Riesgos de " : "Risks of "}
              <span>{language === "es" ? "No Tener" : "Not Having"}</span>
              {language === "es" ? " Seguro Medico" : " Health Insurance"}
            </h2>
            <p>
              {language === "es"
                ? "Te ayudamos a ahorrar tiempo y dinero. Comparamos por ti cual es el seguro que mas te conviene."
                : "We help you save time and money. We compare which insurance best fits your needs."}
            </p>
          </div>
          <div className="health-card-grid three">
            {riskCards.map((item) => (
              <article key={item.title} className="health-info-card danger">
                <div className="health-icon-circle danger">!</div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
          <div className="health-centered-cta">
            <button type="button" className="health-cta-button" onClick={handleScrollToWizard}>
              {language === "es" ? "Obten Una Cotizacion" : "Get A Quote"}
            </button>
          </div>
        </div>
      </section>

      <section className="health-section muted">
        <div className="health-container health-narrow">
          <div className="health-section-header">
            <p className="health-overline" style={{ textTransform: "none", letterSpacing: "normal", color: "#6B7280", margin: "0 0 32px" }}>
              {language === "es"
                ? "Hay 4 niveles de cobertura diferentes, a menudo denominados \"planes de metal\". Todos ofrecen los mismos beneficios esenciales para la salud. La principal diferencia es cuánto paga por la atención en comparación con lo que cubre su plan."
                : "There are 4 different levels of coverage, often called \"metal plans\". They all offer the same essential health benefits. The main difference is how much you pay for care compared to what your plan covers."}
            </p>
          </div>
          <div className="lovable-benefits-card">
            <h2>
              <span className="text-green-strong">{language === "es" ? "Seguro de Salud " : "Health Insurance "}</span>
              {language === "es" ? "te facilita ahorrar" : "makes it easy to save"}
            </h2>
            <div className="lovable-benefits-list">
              {premiumBenefits.map((item) => (
                <div key={item.title} className="lovable-benefit-item">
                  <div className="lovable-benefit-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                  </div>
                  <div className="lovable-benefit-text">
                    <strong>{item.title}</strong>
                    <p>{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="health-section">
        <div className="health-container">
          <div className="health-section-header">
            <h2>{language === "es" ? "4 Niveles de Cobertura Medica" : "4 Levels of Medical Coverage"}</h2>
            <p>
              {language === "es"
                ? "Los niveles de metal determinan cuanto paga su plan de salud y cuanto paga usted."
                : "Metal tiers determine how much your health plan pays and how much you pay."}
            </p>
          </div>
          <div className="health-card-grid four">
            {metalTiers.map((tier) => (
              <article key={tier.name} className={`health-tier-card tier-${tier.id}`}>
                <div className="health-tier-badge">{tier.name.charAt(0)}</div>
                <h3>{tier.name}</h3>
                <div className="health-tier-metrics">
                  <div>
                    <span>{language === "es" ? "Compañía paga" : "Plan pays"}</span>
                    <strong className="tier-plan-pays">{tier.planPays}</strong>
                  </div>
                  <div>
                    <span>{language === "es" ? "Tú pagas" : "You pay"}</span>
                    <strong className="tier-you-pay">{tier.youPay}</strong>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="health-section">
        <div className="health-container lovable-fpl-section">
          <div className="lovable-fpl-badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"></polyline></svg>
            <span>{language === "es" ? "4 de 5 califican" : "4 out of 5 qualify"}</span>
          </div>
          <h2 className="lovable-fpl-title">
            {language === "es" ? "¿Calificas para un Plan de $0?" : "Do you qualify for a $0 Plan?"}
          </h2>
          <p className="lovable-fpl-subtitle">
            {language === "es"
              ? "La elegibilidad se basa en el ingreso de tu hogar. Si tu ingreso está dentro de estos rangos, podrías calificar para un plan sin costo mensual."
              : "Eligibility is based on household income. If your income falls within these ranges, you may qualify for a plan with no monthly cost."}
          </p>

          <div className="lovable-fpl-card">
            <div className="lovable-fpl-table-wrapper">
              <table className="lovable-fpl-table">
                <thead>
                  <tr>
                    {fplTableHeaders.map((header, idx) => (
                      <th key={header} className={idx === 3 ? "highlight-col-header" : ""}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fplTableRows.map((row) => (
                    <tr key={row.size}>
                      <td><strong>{row.size}</strong></td>
                      <td>{row.fpl100}</td>
                      <td>{row.fpl150}</td>
                      <td className="highlight-col-cell"><strong>{row.fpl200}</strong></td>
                      <td>{row.fpl400}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="lovable-fpl-footer">
              <div className="lovable-fpl-footer-text">
                <div className="lovable-fpl-footer-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                </div>
                <p>
                  <strong>{language === "es" ? "★ Columna destacada (200% FPL):" : "★ Featured column (200% FPL):"}</strong>{" "}
                  {language === "es"
                    ? "La mayoría de nuestros clientes califican en este rango o menos."
                    : "Most of our clients qualify in this range or below."}
                </p>
              </div>
              <button type="button" className="lovable-cta-btn" onClick={handleScrollToWizard}>
                {language === "es" ? "Verificar Elegibilidad" : "Check Eligibility"}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="btn-arrow-icon"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
              </button>
            </div>
          </div>
          <p className="lovable-fpl-disclaimer">
            {language === "es"
              ? "Nota: Los niveles exactos de ingreso pueden variar por estado. Datos basados en las guías federales de pobreza 2024."
              : "Note: Exact income levels may vary by state. Data based on 2024 federal poverty guidelines."}
          </p>
        </div>
      </section>

      <main className="app-body">
        <section className="page-content health-form-section" id="quote-wizard">
          <div className="health-section-header centered">
            <h2>
              {language === "es" ? "Obten tu " : "Get your "}
              <span>{language === "es" ? "Cotizacion Gratis" : "Free Quote"}</span>
            </h2>
            <p>
              {language === "es"
                ? "Completa los pasos y recibe acompanamiento para encontrar el mejor plan."
                : "Complete the steps and get support finding the best plan."}
            </p>
          </div>
          {apiError && <div className="form-error major-error">{apiError}</div>}
          {/* Visual phase stepper */}
          {wizardStep !== "quoteSuccess" && (
            <div className="wizard-progress">
              <div className="wizard-stepper" role="progressbar" aria-label="Progreso del formulario">
                <div
                  className="wizard-stepper-fill"
                  style={{ width: `${stepperFillPct}%` }}
                />
                {visiblePhases.map((phase, idx) => {
                  const isDone = idx < currentPhaseIndex;
                  const isActive = idx === currentPhaseIndex;
                  return (
                    <div
                      key={phase.key}
                      className={`wizard-phase${isDone ? " done" : isActive ? " active" : ""}`}
                    >
                      <div className={`wizard-phase-dot${isDone ? " done" : isActive ? " active" : ""}`}>
                        {isDone ? "✓" : idx + 1}
                      </div>
                      <div className="wizard-phase-name">{phase.label}</div>
                    </div>
                  );
                })}
              </div>
              {currentPhaseIndex >= 0 && (
                <div className="wizard-step-counter">
                  {t("wizard.stepCounter", {
                    current: String(currentPhaseIndex + 1),
                    total: String(visiblePhases.length),
                    phase: visiblePhases[currentPhaseIndex]?.label ?? "",
                  })}
                </div>
              )}
            </div>
          )}
          <div className="card-wizard">
            <section className="page-heading">
              <div>
                <h2>{pageTitle}</h2>
              </div>
            </section>
            {wizardStep === "householdType" && (
              <HouseholdTypeStep
                value={householdType}
                onSelect={handleSelectHouseholdType}
              />
            )}

            {wizardStep === "householdBasics" && householdType && (
              <HouseholdBasicsStep
                householdType={householdType}
                initialZip={metadata.household.zipCode}
                selectedHouseholdTypeIcon={selectedHouseholdTypeIcon}
                initialIncome={metadata.household.income}
                initialCountyFips={metadata.household.countyFips}
                initialEffectiveDate={metadata.household.effectiveDate}
                onBack={() => setWizardStep("householdType")}
                onSubmit={handleHouseholdBasicsSubmit}
              />
            )}

            {wizardStep === "primary" && householdType && (
              <PrimaryApplicantStep
                selectedHouseholdTypeIcon={selectedHouseholdTypeIcon}
                householdType={householdType}
                initialValues={currentPrimary}
                onBack={() => setWizardStep("householdBasics")}
                onSubmit={handlePrimarySubmit}
              />
            )}

            {wizardStep === "members" && householdType && (
              <AdditionalMembersStep
                selectedHouseholdTypeIcon={selectedHouseholdTypeIcon}
                householdType={householdType}
                members={metadata.members}
                onBack={() => setWizardStep("primary")}
                onSubmit={handleMembersSubmit}
              />
            )}

            {wizardStep === "identityOtp" && (
              <QuoteOtpStep
                onSend={handleRequestIdentityOtp}
                onVerify={handleVerifyIdentityOtp}
                isSending={isIdentityOtpSending}
                isVerifying={isIdentityOtpVerifying}
                otpSent={identityOtpStatus.sent}
                smsSent={identityOtpStatus.smsSent}
                emailSent={identityOtpStatus.emailSent}
                error={apiError}
                leadEmail={currentPrimary?.email ?? null}
                leadPhone={currentPrimary?.phone ?? null}
                translationPrefix="identity.otp"
              />
            )}

            {wizardStep === "plans" && currentPrimary && (
              <>
                <PlanSelectionPage
                  metadata={metadata}
                  onBack={() =>
                    householdNeedsAdditionalMembers(householdType)
                      ? setWizardStep("members")
                      : setWizardStep("primary")
                  }
                  onPlanSelect={handlePlanSelect}
                />
                <div className="social-proof-bar" style={{ marginBottom: "24px" }}>
                  <div className="social-proof-item">
                    <span className="social-proof-icon">🔒</span>
                    <span>
                      {language === "es" ? "Gratis y sin compromiso" : "Free & no commitment"}
                    </span>
                  </div>
                  <div className="social-proof-divider" />
                  <div className="social-proof-item">
                    <span className="social-proof-icon">🛡️</span>
                    <span>
                      {language === "es" ? "256-bit encriptado" : "256-bit encrypted"}
                    </span>
                  </div>
                  <div className="social-proof-divider" />
                  <div className="social-proof-item">
                    <span className="social-proof-icon">✅</span>
                    <span>
                      {language === "es" ? "Agente licenciado ACA" : "Licensed ACA agent"}
                    </span>
                  </div>
                </div>
              </>
            )}

            {wizardStep === "summary" && selectedPlan && currentPrimary && (
              <PlanSummaryPage
                metadata={metadata}
                plan={selectedPlan}
                primary={currentPrimary}
                consentStatus={consentStatus}
                language={language}
                signingLink={signingLink}
                consentHtml={consentHtml}
                onBack={() => setWizardStep("plans")}
                onConfirm={handleConfirmPlan}
                onCheckConsent={
                  consentStatus === "SIGNED"
                    ? undefined
                    : () => handleCheckConsent(false)
                }
                isSubmitting={isSubmitting}
                isCheckingConsent={isCheckingConsent}
              />
            )}

            {wizardStep === "consent" && selectedPlan && currentPrimary && (
              <PlanSummaryPage
                metadata={metadata}
                plan={selectedPlan}
                primary={currentPrimary}
                consentStatus={consentStatus}
                signingLink={signingLink}
                consentHtml={consentHtml}
                onBack={() => setWizardStep("summary")}
                onConfirm={() => { }}
                onCheckConsent={() => handleCheckConsent(false)}
                isSubmitting={false}
                isCheckingConsent={isCheckingConsent}
              />
            )}

            {wizardStep === "quoteHolder" && (
              <QuoteHolderStep
                selectedHouseholdTypeIcon={selectedHouseholdTypeIcon}
                initialValues={quoteHolderState}
                onSubmit={handleQuoteHolderSubmit}
                onBack={() => setWizardStep("consent")}
                isSubmitting={false}
              />
            )}

            {wizardStep === "quoteMembers" && (
              <QuoteMembersStep
                selectedHouseholdTypeIcon={selectedHouseholdTypeIcon}
                initialMembers={quoteMembersState}
                onSubmit={handleQuoteMembersSubmit}
                onBack={() => setWizardStep("quoteHolder")}
                isSubmitting={false}
              />
            )}

            {wizardStep === "quotePayment" && (
              <QuotePaymentStep
                initialValues={quotePaymentState}
                onSubmit={handleQuotePaymentSubmit}
                onBack={() =>
                  quoteMembersState.length > 0
                    ? setWizardStep("quoteMembers")
                    : setWizardStep("quoteHolder")
                }
                isSubmitting={isCreatingQuote}
                error={apiError}
              />
            )}

            {wizardStep === "quoteSuccess" && (
              <QuoteSuccessStep
                policyId={quotePolicyId}
                selectedHouseholdTypeIcon={selectedHouseholdTypeIcon}
                planName={successPlanName}
                planCarrier={successPlanCarrier}
                planPremium={
                  successPlanPremium !== null &&
                    Number.isFinite(successPlanPremium)
                    ? successPlanPremium
                    : null
                }
                onFinish={handleQuoteFinish}
              />
            )}
          </div>

          {isSubmitting && !["summary", "consent"].includes(wizardStep) && (
            <div className="loading-overlay">
              <Spinner animation="border" role="status" />
              <span>{t("wizard.loading")}</span>
            </div>
          )}
        </section>
      </main>

      <section className="health-section muted">
        <div className="health-container">
          <div className="health-section-header">
            <h2>{language === "es" ? "Lo Que Dicen Nuestros Clientes" : "What Our Clients Say"}</h2>
            <p>
              {language === "es"
                ? "Miles de familias ya confian en nosotros para proteger su salud."
                : "Thousands of families already trust us to protect their health."}
            </p>
          </div>
          <div className="health-card-grid three">
            {testimonials.map((item) => (
              <article key={item.name} className="health-testimonial-card">
                <div className="health-stars">★★★★★</div>
                <p>"{item.quote}"</p>
                <div className="health-testimonial-user">
                  <img src={item.img} alt={item.name} />
                  <div>
                    <strong>{item.name}</strong>
                    <span>{item.city}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="health-section">
        <div className="health-container health-narrow">
          <div className="health-section-header">
            <p className="health-overline">{language === "es" ? "Proceso Simple" : "Simple Process"}</p>
            <h2>
              {language === "es" ? "Inscribete en " : "Enroll in "}
              <span>{language === "es" ? "3 Pasos" : "3 Steps"}</span>
            </h2>
            <p>{language === "es" ? "Te guiamos en cada paso del camino." : "We guide you every step of the way."}</p>
          </div>
          <div className="health-steps-grid">
            {stepsContent.map((item) => (
              <article key={item.number} className="health-step-card">
                <div className="health-step-number">{item.number}</div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="health-section">
        <div className="health-container health-faq">
          <div className="health-section-header">
            <p className="health-overline">{language === "es" ? "Preguntas Frecuentes" : "Frequently Asked Questions"}</p>
            <h2>
              {language === "es" ? "Tienes " : "Have "}
              <span>{language === "es" ? "Dudas?" : "Questions?"}</span>
            </h2>
          </div>
          <div className="health-faq-list">
            {faqItems.map((item) => (
              <details key={item.q} className="health-faq-item">
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="health-final-cta">
        <div className="health-container health-final-cta-inner">
          <h2>{language === "es" ? "Listo Para Empezar?" : "Ready to Get Started?"}</h2>
          <p>
            {language === "es"
              ? "Habla con un agente licenciado hoy y encuentra el plan perfecto para ti y tu familia."
              : "Speak with a licensed agent today and find the right plan for you and your family."}
          </p>
          <div className="health-hero-actions centered">
            <a
              className="health-call-button"
              href={activePhoneHref}
              onClick={() => trackPhoneCall(activePhone, language, conversionIdRef.current)}
            >
              <FontAwesomeIcon icon={faPhone} />
              <span>{activePhone}</span>
            </a>
            <button type="button" className="health-outline-button" onClick={handleScrollToWizard}>
              {language === "es" ? "Obten Una Cotizacion" : "Get A Quote"}
            </button>
          </div>
        </div>
      </section>

      <footer className="app-footer health-footer">
        <div className="footer-container">
          <div className="footer-top">
            <div className="footer-brand">
              <img src="/media/logo-light.png" alt="The Future Insurance" className="footer-logo" />
              <p className="footer-friendly-text">{t("footer.friendlyText")}</p>
            </div>
          </div>

          <div className="footer-divider"></div>

          <div className="footer-bottom">
            <div className="footer-disclaimer">
              <p>{t("footer.line1")}</p>
              <p>{t("footer.line2")}</p>
              <p>{t("footer.line3")}</p>
            </div>
            <div className="footer-copyright">
              <p>{t("footer.line4", { year: new Date().getFullYear() })}</p>
            </div>
          </div>
        </div>
      </footer>

      <div className="health-sticky-mobile">
        <a
          className="health-call-button"
          href={activePhoneHref}
          onClick={() => trackPhoneCall(activePhone, language, conversionIdRef.current)}
        >
          <FontAwesomeIcon icon={faPhone} />
          <span>{activePhone}</span>
        </a>
        <button type="button" className="health-outline-button green" onClick={handleScrollToWizard}>
          {language === "es" ? "INSCRIBIRME" : "ENROLL NOW"}
        </button>
      </div>

      {
        process.env.REACT_APP_WHATSAPP_NUMBER && (
          <a
            href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}`}
            target="_blank"
            rel="noopener noreferrer"
            className="whatsapp-floating-button"
            aria-label="Contact us on WhatsApp"
          >
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              className="whatsapp-icon"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
          </a>
        )
      }
    </div>
  );
};

export default App;

